/**
 * Global-e API Client
 * Handles API calls to Global-e shipping details endpoint via Shopify App Proxy
 * Includes caching and request deduplication
 * 
 * All requests go through Shopify App Proxy: /apps/api/globale-shipping-details
 */

// HTTP Constants
const CONTENT_TYPE_JSON = 'application/json';
const HTTP_METHOD_POST = 'POST';

// Error Constants
const ERROR_FAILED_TO_FETCH = 'Failed to fetch';
const ERROR_TYPE_ERROR = 'TypeError';
const ERROR_UNKNOWN_API = 'Unknown error from Global-e API';

// Response Constants
const STATUS_SUCCESS = 'success';
const PROXY_URL = '/apps/api/globale-shipping-details';

/**
 * @typedef {Object} ShippingDetailsParams
 * @property {string} country
 * @property {number} amount
 * @property {string} currency
 * @property {string[]} productCodes
 * @property {HTMLElement|null} [element]
 */

class GlobaleApiClient {
  constructor() {
    /** @type {Map<string, {data: any, timestamp: number}>} */
    this.cache = new Map();
    /** @type {Map<string, Promise<any>>} */
    this.pendingRequests = new Map();
    this.CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get shipping details with caching and request deduplication
   * @param {ShippingDetailsParams} params
   * @returns {Promise<Object>}
   */
  async getShippingDetails(params) {
    const cacheKey = this.getCacheKey(params);

    const cached = this.getCached(cacheKey);
    if (cached) {
      return cached;
    }

    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const request = this.fetchShippingDetails(params)
      .then(data => {
        this.setCache(cacheKey, data);
        this.pendingRequests.delete(cacheKey);
        return data;
      })
      .catch(error => {
        this.pendingRequests.delete(cacheKey);
        throw error;
      });

    this.pendingRequests.set(cacheKey, request);
    return request;
  }

  /**
   * Fetch shipping details from Global-e API via proxy
   * @param {ShippingDetailsParams} params
   * @returns {Promise<Array<Object>>}
   */
  async fetchShippingDetails(params) {
    const requestBody = {
      destinationCountry: params.country,
      amount: params.amount,
      currencyCode: params.currency,
      productCodes: params.productCodes,
    };

    return this.#fetchViaProxy(PROXY_URL, requestBody);
  }

  /**
   * Fetch via proxy endpoint
   * @param {string} proxyUrl
   * @param {{destinationCountry: string, amount: number, currencyCode: string, productCodes: string[]}} requestBody
   * @returns {Promise<Array<Object>>}
   */
  async #fetchViaProxy(proxyUrl, requestBody) {
    const fullUrl = this.#buildFullUrl(proxyUrl);

    const response = await this.#makeRequest(fullUrl, requestBody);
    await this.#validateResponse(response, fullUrl);
    
    const result = await this.#parseResponse(response);
    return this.#processResponse(result, requestBody.currencyCode);
  }

  /**
   * Build full URL from relative or absolute URL
   * @param {string} url
   * @returns {string}
   */
  #buildFullUrl(url) {
    if (url.startsWith('http')) {
      return url;
    }

    if (typeof window === 'undefined' || !window.location) {
      throw new Error('Cannot determine URL: window is not available');
    }

    return `${window.location.origin}${url}`;
  }

  /**
   * Make fetch request
   * @param {string} url
   * @param {Object} requestBody
   * @returns {Promise<Response>}
   */
  async #makeRequest(url, requestBody) {
    try {
      return await fetch(url, {
        method: HTTP_METHOD_POST,
        headers: { 
          'Content-Type': CONTENT_TYPE_JSON,
          'Accept': CONTENT_TYPE_JSON
        },
        body: JSON.stringify(requestBody)
      });
    } catch (fetchError) {
      this.#handleFetchError(fetchError, url);
      throw fetchError; // Never reached, but for type safety
    }
  }

  /**
   * Handle fetch errors
   * @param {Error} error
   * @param {string} url
   */
  #handleFetchError(error, url) {
    if (error?.message?.includes(ERROR_FAILED_TO_FETCH) || error?.name === ERROR_TYPE_ERROR) {
      throw new Error(`Network error: Cannot connect to proxy at ${url}. Please ensure proxy server is running.`);
    }
    throw new Error(`Proxy request failed: ${error.message}`);
  }

  /**
   * Validate HTTP response
   * @param {Response} response
   * @param {string} url
   */
  async #validateResponse(response, url) {
    if (response.ok) return;

    const errorText = await response.text().catch(() => '');
    
    if (response.status === 404) {
      throw new Error('Proxy endpoint not found. Please check proxy URL configuration.');
    }
    if (response.status === 500) {
      throw new Error('Proxy server error. Please check backend configuration.');
    }
    
    throw new Error(`Proxy error: ${response.status} - ${errorText || 'Unknown error'}`);
  }

  /**
   * Parse JSON response
   * @param {Response} response
   * @returns {Promise<any>}
   */
  async #parseResponse(response) {
    try {
      const text = await response.text();
      if (!text?.trim()) {
        throw new Error('Empty response from proxy');
      }
      return JSON.parse(text);
    } catch (parseError) {
      throw new Error(`Failed to parse proxy response: ${parseError.message}`);
    }
  }

  /**
   * Process and normalize API response
   * @param {any} result
   * @param {string} [requestCurrencyCode]
   * @returns {Array<Object>}
   */
  #processResponse(result, requestCurrencyCode) {
    const apiResponse = result.success ? result.data : result;
    const normalized = Array.isArray(apiResponse) ? apiResponse[0] : apiResponse;

    if (normalized.error || (normalized.shippingRates === undefined && normalized.status !== STATUS_SUCCESS)) {
      throw new Error(normalized.error || normalized.message || ERROR_UNKNOWN_API);
    }

    return this.#normalizeResponse(normalized, requestCurrencyCode);
  }

  /**
   * Normalize API response to consistent format
   * @param {any} data
   * @param {string} [requestCurrencyCode] - Currency code from request (fallback if not in response)
   * @returns {Array<Object>}
   */
  #normalizeResponse(data, requestCurrencyCode) {
    const currencyCode = data.currencyCode || 
                        data.shippingRates?.[0]?.shippingCostCurrency || 
                        requestCurrencyCode;

    return [{
      destinationCountry: data.destinationCountry,
      status: data.status || STATUS_SUCCESS,
      shippingRates: data.shippingRates || [],
      currencyCode
    }];
  }

  /**
   * Generate cache key from params
   * @param {ShippingDetailsParams} params
   * @returns {string}
   */
  getCacheKey(params) {
    const codes = [...params.productCodes].sort().join(',');
    return `${params.country}_${params.amount}_${params.currency}_${codes}`;
  }

  /**
   * Get cached data if available and not expired
   * @param {string} key
   * @returns {any|null}
   */
  getCached(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp >= this.CACHE_TTL;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  /**
   * Set cache with timestamp
   * @param {string} key
   * @param {Object} data
   */
  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }
}

export const globaleApiClient = new GlobaleApiClient();
